import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import { CAPABILITY_META, CapabilityMeta } from 'src/DeviceIntegrationManagement/MockData/capabilities';
import { CapabilityKey } from 'src/DeviceIntegrationManagement/MockData/types';

interface Props {
  capabilities: string[];
  /** Max chips before collapsing the rest into a `+N` chip. */
  max?: number;
}

/** Rich hover-card content for a single capability: icon tile + name + description. */
const CapabilityHoverCard = ({ meta }: { meta: CapabilityMeta }): React.JSX.Element => {
  const Icon = meta.icon;
  return (
    <Stack spacing={1} sx={{ p: 0.5, maxWidth: 260 }}>
      <Stack direction={'row'} spacing={1} alignItems={'center'}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
            '& .MuiSvgIcon-root': { fontSize: 16 },
          }}
        >
          <Icon />
        </Box>
        <Typography variant={'subtitle2'} fontWeight={700}>
          {meta.label}
        </Typography>
      </Stack>
      <Typography variant={'caption'} color={'text.secondary'}>
        {meta.description}
      </Typography>
    </Stack>
  );
};

/** One icon-only capability chip. */
const CapabilityChip = ({ capability, scale = 1 }: { capability: string; scale?: number }): React.JSX.Element => {
  const meta = CAPABILITY_META[capability as CapabilityKey];
  const Icon = meta.icon;
  return (
    <TooltipHardened arrow tooltip={<CapabilityHoverCard meta={meta} />}>
      <Box
        sx={{
          width: 26 * scale,
          height: 26 * scale,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          transition: 'border-color 120ms, color 120ms, background-color 120ms',
          '& .MuiSvgIcon-root': { fontSize: 'large' },
        }}
      >
        <Icon fontSize={'large'} />
      </Box>
    </TooltipHardened>
  );
};

/**
 * A wrapping row of icon-only capability chips. Each chip shows a rich hover
 * card (icon + name + description); overflow collapses into a `+N` chip whose
 * hover card lists the remaining capabilities.
 */
export const CapabilityChips = ({ capabilities, max = 8 }: Props): React.JSX.Element => {
  const ordered = capabilities.filter((key) => key in CAPABILITY_META).sort();
  const visible = ordered.slice(0, max);
  const overflow = ordered.slice(max);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {visible.map((key) => (
        <CapabilityChip key={key} capability={key} scale={1.4} />
      ))}
      {overflow.length > 0 && (
        <Tooltip
          arrow
          title={
            <Stack spacing={1} sx={{ p: 0.5 }}>
              {overflow.map((key) => {
                const meta = CAPABILITY_META[key as CapabilityKey];
                const Icon = meta.icon;
                return (
                  <Stack key={key} direction={'row'} spacing={1} alignItems={'center'}>
                    <Icon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography variant={'caption'} fontWeight={600}>
                      {meta.label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          }
        >
          <Box
            sx={{
              height: 26,
              minWidth: 26,
              px: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.secondary',
              fontSize: 11,
              fontWeight: 700,
              transition: 'border-color 120ms, color 120ms',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            +{overflow.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
